import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { recordApiResult } from '../usage/usageRecorder';

/**
 * Klient HTTP do API NestJS.
 *
 * Port z FreightFusion (`apps/app/src/api/axiosNest.ts`, stan z 15.09.2026).
 * Zapis pochodzenia: PORT-FROM-TLSAPP.md
 *
 * Czego NIE przeniesiono i dlaczego:
 *
 * 1. Odświeżanie tokena przy 401. Źródło woła `POST /auth/refresh`, a backend
 *    Marathona ma dziś wyłącznie `POST /auth/login` — ten endpoint nie istnieje.
 *    Przeniesienie tej logiki dałoby martwy kod, który przy pierwszym 401
 *    wysłałby żądanie w próżnię i dopiero potem wylogował użytkownika.
 *    Zamiast tego czyścimy token i odsyłamy na logowanie. Gdy backend dostanie
 *    `/auth/refresh`, wracamy tu i dokładamy ponowienie żądania.
 *
 * 2. Tryb podszywania się pod użytkownika (`sessionStorage` obok
 *    `localStorage`). Cała ta gałąź istnieje na potrzeby panelu
 *    wieloklienckiego, którego w instancji Marathona nie budujemy —
 *    T.01 mówi wprost: „jedna firma na instancję, bez wspólnego panelu
 *    administracyjnego". Token trzymamy w `localStorage`, spójnie
 *    z istniejącym `authProvider.ts`.
 */

// Odstępstwo od źródła: TLSApp rzuca tu wyjątkiem przy imporcie. To wywraca
// build i testy wszędzie, gdzie nie ma pliku .env — a jest on gitignorowany,
// więc w CI go nie ma. Repo Marathona ma już własną konwencję (App.tsx,
// authProvider.ts): awaryjny adres lokalny.
const NEST_API_URL =
  process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';

export const TOKEN_KEY = 'token';

/** Ścieżka, na którą odsyłamy po utracie sesji. */
export const LOGIN_PATH = '/login';

const axiosNest = axios.create({ baseURL: NEST_API_URL });

/**
 * Chwila wysłania żądania — do czasu trwania działania w pomiarze użycia
 * (pełny ślad, 23.10.2026). Mapa słaba po obiekcie konfiguracji: axios
 * oddaje ten sam obiekt w odpowiedzi i błędzie, a nic nie zostaje w pamięci.
 */
const startedAt = new WeakMap<object, number>();

const elapsed = (config: object | undefined): number => {
  const start = config ? startedAt.get(config) : undefined;
  return start === undefined ? 0 : Date.now() - start;
};

// Token doklejamy do każdego żądania.
axiosNest.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  startedAt.set(config, Date.now());
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 oznacza utratę sesji: czyścimy token i odsyłamy na logowanie.
// Wyjątek: żądania do `auth/` — tam 401 to po prostu błędne dane logowania
// i obsługuje go formularz, a nie globalne wylogowanie.
axiosNest.interceptors.response.use(
  (response) => {
    // Pomiar użycia (pełny ślad 23.10.2026): działanie z kodem i czasem — bez treści.
    recordApiResult(response.config?.method, response.config?.url, response.status, elapsed(response.config));
    return response;
  },
  (error: AxiosError) => {
    const url = error.config?.url ?? '';
    const isAuthEndpoint = url.includes('auth/');
    // Pomiar użycia (S-22.10-02): sam kod, metoda i ścieżka — bez treści błędu;
    // działanie zmieniające dane także bez odpowiedzi (kod pusty).
    recordApiResult(error.config?.method, url, error.response?.status, elapsed(error.config));

    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem(TOKEN_KEY);
      if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
        window.location.href = LOGIN_PATH;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosNest;
